import {translate} from '@docusaurus/Translate';
export const AGENT_API_BASE_PATH = '/api/v1/agent';
const AGENT_LANGUAGE = translate({id: 'site.languageTag', message: 'zh-CN'});

export type QualityStatus = 'passed' | 'needs_review' | 'check_failed';

export type QualityReason =
  | 'supported'
  | 'missing_evidence'
  | 'unsupported_claim'
  | 'off_topic'
  | 'incomplete'
  | 'uncertain'
  | 'checker_error';

export type FeedbackRating = 'helpful' | 'unhelpful';

export type FeedbackReason = 'incorrect' | 'irrelevant' | 'incomplete' | 'outdated' | 'unclear';

export type ChatRequest = {
  message: string;
  session_id?: string;
  page_title?: string;
  page_path?: string;
};

export type TokenUsageResponse = {
  input_tokens?: number | null;
  output_tokens?: number | null;
  total_tokens?: number | null;
  cached_input_tokens?: number | null;
  model_calls: number;
  complete: boolean;
};

export type ChatResponse = {
  reply: string;
  session_id: string;
  message_id: string;
  quality_status: QualityStatus;
  quality_reason: QualityReason;
  recommended_questions: string[];
  token_usage: TokenUsageResponse;
};

export type ChatStreamSession = Pick<ChatResponse, 'session_id' | 'message_id'>;

export type ChatStreamQuality = Pick<ChatResponse, 'quality_status' | 'quality_reason'>;

export type ChatStreamRecommendations = Pick<ChatResponse, 'recommended_questions'>;

export type ChatStreamHandlers = {
  onSession?: (session: ChatStreamSession) => void;
  onChunk?: (content: string) => void;
  onQuality?: (quality: ChatStreamQuality) => void;
  onRecommendations?: (recommendations: ChatStreamRecommendations) => void;
  onUsage?: (usage: TokenUsageResponse) => void;
};

export type FeedbackRequest = {
  rating: FeedbackRating;
  reason?: FeedbackReason;
};

export type FeedbackResponse = {
  message_id: string;
  rating: FeedbackRating;
  reason?: FeedbackReason | null;
};

export type ApiResponse<T> = {
  code?: number;
  messageKey?: string | null;
  msg?: string;
  data: T;
};

export type AgentErrorKind =
  | 'invalid_request'
  | 'unauthorized'
  | 'forbidden'
  | 'context_limit'
  | 'rate_limited'
  | 'unavailable'
  | 'network'
  | 'invalid_response'
  | 'cancelled'
  | 'unknown';

export class AgentApiError extends Error {
  readonly kind: AgentErrorKind;
  readonly status?: number;
  readonly retryable: boolean;

  constructor(kind: AgentErrorKind, message: string, options?: {status?: number; retryable?: boolean}) {
    super(message);
    this.name = 'AgentApiError';
    this.kind = kind;
    this.status = options?.status;
    this.retryable = options?.retryable ?? false;
  }
}

const qualityStatuses = new Set<QualityStatus>(['passed', 'needs_review', 'check_failed']);
const qualityReasons = new Set<QualityReason>([
  'supported',
  'missing_evidence',
  'unsupported_claim',
  'off_topic',
  'incomplete',
  'uncertain',
  'checker_error',
]);
const feedbackRatings = new Set<FeedbackRating>(['helpful', 'unhelpful']);
const feedbackReasons = new Set<FeedbackReason>([
  'incorrect',
  'irrelevant',
  'incomplete',
  'outdated',
  'unclear',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isOptionalNullableNonNegativeInteger(
  value: unknown,
): value is number | null | undefined {
  return value === undefined || value === null || (Number.isInteger(value) && Number(value) >= 0);
}

function parseRecommendedQuestions(value: unknown): string[] {
  if (
    !Array.isArray(value) ||
    value.length !== 3 ||
    !value.every(
      (question) =>
        isString(question) && question.trim().length > 0 && Array.from(question).length <= 200,
    )
  ) {
    throw new AgentApiError('invalid_response', translate({message: '智能助理返回了无效的推荐问题。'}));
  }

  return [...value];
}

function parseTokenUsage(value: unknown): TokenUsageResponse {
  if (
    !isRecord(value) ||
    !isOptionalNullableNonNegativeInteger(value.input_tokens) ||
    !isOptionalNullableNonNegativeInteger(value.output_tokens) ||
    !isOptionalNullableNonNegativeInteger(value.total_tokens) ||
    !isOptionalNullableNonNegativeInteger(value.cached_input_tokens) ||
    !Number.isInteger(value.model_calls) ||
    Number(value.model_calls) < 0 ||
    typeof value.complete !== 'boolean'
  ) {
    throw new AgentApiError('invalid_response', translate({message: '智能助理返回了无效的 Token 用量。'}));
  }

  if (
    value.complete &&
    (typeof value.input_tokens !== 'number' ||
      typeof value.output_tokens !== 'number' ||
      typeof value.total_tokens !== 'number' ||
      value.total_tokens !== value.input_tokens + value.output_tokens)
  ) {
    throw new AgentApiError('invalid_response', translate({message: '智能助理返回的 Token 用量不完整。'}));
  }

  return {
    input_tokens: value.input_tokens,
    output_tokens: value.output_tokens,
    total_tokens: value.total_tokens,
    cached_input_tokens: value.cached_input_tokens,
    model_calls: value.model_calls as number,
    complete: value.complete,
  };
}

function getResponseMessage(payload: unknown, fallback: string): string {
  if (isRecord(payload)) {
    if (isString(payload.msg) && payload.msg.trim()) {
      return payload.msg;
    }
    if (isString(payload.detail) && payload.detail.trim()) {
      return payload.detail;
    }
  }

  if (isString(payload) && payload.trim()) {
    return payload;
  }

  return fallback;
}

function mapStreamError(event: Record<string, unknown>): AgentApiError {
  if (event.messageKey === 'agent.context_limit_exceeded') {
    return new AgentApiError(
      'context_limit',
      getResponseMessage(event, translate({message: '会话上下文过长，请缩短当前输入或创建新会话。'})),
      {status: 409},
    );
  }

  return new AgentApiError(
    'unknown',
    getResponseMessage(event, translate({message: '智能助理生成回答时发生错误。'})),
    {retryable: true},
  );
}

function mapHttpError(response: Response, payload: unknown): AgentApiError {
  const status = response.status;
  const fallback = translate({message: 'Agent 请求失败（HTTP {value0}）'}, {value0: status});
  const message = getResponseMessage(payload, fallback);

  if (status === 400 || status === 422) {
    return new AgentApiError('invalid_request', message, {status});
  }
  if (status === 401) {
    return new AgentApiError('unauthorized', message, {status});
  }
  if (status === 403) {
    return new AgentApiError('forbidden', message, {status});
  }
  if (status === 409) {
    return new AgentApiError('context_limit', message, {status});
  }
  if (status === 429) {
    return new AgentApiError('rate_limited', message, {status, retryable: true});
  }
  if (status === 503) {
    return new AgentApiError('unavailable', message, {status, retryable: true});
  }

  return new AgentApiError('unknown', message, {
    status,
    retryable: status >= 500,
  });
}

async function readPayload(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    if (!response.ok) {
      return text;
    }
    throw new AgentApiError('invalid_response', translate({message: 'Agent 返回了无法解析的响应。'}));
  }
}

async function requestAgent(path: string, init: RequestInit): Promise<unknown> {
  let response: Response;

  try {
    response = await fetch(`${AGENT_API_BASE_PATH}${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        'accept-language': AGENT_LANGUAGE,
        'content-type': 'application/json',
        ...init.headers,
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new AgentApiError('cancelled', translate({message: '请求已取消。'}));
    }
    throw new AgentApiError('network', translate({message: '无法连接智能助理服务。'}), {retryable: true});
  }

  const payload = await readPayload(response);

  if (!response.ok) {
    throw mapHttpError(response, payload);
  }

  return payload;
}

function getEnvelopeData(payload: unknown): Record<string, unknown> {
  if (!isRecord(payload)) {
    throw new AgentApiError('invalid_response', translate({message: 'Agent 响应缺少标准数据结构。'}));
  }

  if (typeof payload.code === 'number' && payload.code !== 0) {
    throw new AgentApiError('unknown', getResponseMessage(payload, translate({message: 'Agent 未能完成请求。'})));
  }

  if (!isRecord(payload.data)) {
    throw new AgentApiError('invalid_response', translate({message: 'Agent 响应缺少 data 字段。'}));
  }

  return payload.data;
}

function parseFeedbackResponse(payload: unknown): FeedbackResponse {
  const data = getEnvelopeData(payload);

  if (
    !isString(data.message_id) ||
    !isString(data.rating) ||
    !feedbackRatings.has(data.rating as FeedbackRating) ||
    (data.reason !== null &&
      data.reason !== undefined &&
      (!isString(data.reason) || !feedbackReasons.has(data.reason as FeedbackReason)))
  ) {
    throw new AgentApiError('invalid_response', translate({message: 'Agent 反馈响应字段不完整。'}));
  }

  return {
    message_id: data.message_id,
    rating: data.rating as FeedbackRating,
    reason: data.reason as FeedbackReason | null | undefined,
  };
}

function getSseData(block: string): string | null {
  const dataLines: string[] = [];

  for (const rawLine of block.split(/\r\n|\r|\n/)) {
    const line = rawLine.replace(/^\uFEFF/, '');
    if (line === 'data') {
      dataLines.push('');
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''));
    }
  }

  return dataLines.length > 0 ? dataLines.join('\n') : null;
}

function parseSseJson(data: string): Record<string, unknown> {
  let payload: unknown;

  try {
    payload = JSON.parse(data) as unknown;
  } catch {
    throw new AgentApiError('invalid_response', translate({message: '智能助理返回了无法解析的流式事件。'}));
  }

  if (!isRecord(payload) || !isString(payload.type)) {
    throw new AgentApiError('invalid_response', translate({message: '智能助理流式事件缺少类型。'}));
  }

  return payload;
}

async function readChatStream(
  response: Response,
  handlers: ChatStreamHandlers,
): Promise<ChatResponse> {
  if (!response.body) {
    throw new AgentApiError('invalid_response', translate({message: '智能助理未返回可读取的流。'}));
  }

  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  if (!contentType.includes('text/event-stream')) {
    throw new AgentApiError('invalid_response', translate({message: '智能助理未返回 SSE 流。'}));
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let reply = '';
  let session: ChatStreamSession | null = null;
  let quality: ChatStreamQuality | null = null;
  let recommendations: ChatStreamRecommendations | null = null;
  let tokenUsage: TokenUsageResponse | null = null;
  let receivedDone = false;

  const processBlock = (block: string) => {
    const data = getSseData(block);
    if (data === null) {
      return;
    }
    if (data === '[DONE]') {
      receivedDone = true;
      return;
    }

    const event = parseSseJson(data);
    switch (event.type) {
      case 'session': {
        if (
          session ||
          reply ||
          quality ||
          recommendations ||
          tokenUsage ||
          !isString(event.session_id) ||
          !event.session_id ||
          !isString(event.message_id) ||
          !event.message_id
        ) {
          throw new AgentApiError('invalid_response', translate({message: '智能助理返回了无效的会话事件。'}));
        }
        session = {session_id: event.session_id, message_id: event.message_id};
        handlers.onSession?.(session);
        return;
      }
      case 'chunk': {
        if (!session || quality || recommendations || tokenUsage || !isString(event.content)) {
          throw new AgentApiError('invalid_response', translate({message: '智能助理返回了无效的正文事件。'}));
        }
        reply += event.content;
        handlers.onChunk?.(event.content);
        return;
      }
      case 'quality': {
        if (
          !session ||
          quality ||
          recommendations ||
          tokenUsage ||
          !isString(event.status) ||
          !qualityStatuses.has(event.status as QualityStatus) ||
          !isString(event.reason) ||
          !qualityReasons.has(event.reason as QualityReason)
        ) {
          throw new AgentApiError('invalid_response', translate({message: '智能助理返回了无效的质量事件。'}));
        }
        quality = {
          quality_status: event.status as QualityStatus,
          quality_reason: event.reason as QualityReason,
        };
        handlers.onQuality?.(quality);
        return;
      }
      case 'recommendations': {
        if (!quality || recommendations || tokenUsage) {
          throw new AgentApiError('invalid_response', translate({message: '智能助理返回了无效的推荐问题事件。'}));
        }
        recommendations = {
          recommended_questions: parseRecommendedQuestions(event.recommended_questions),
        };
        handlers.onRecommendations?.(recommendations);
        return;
      }
      case 'usage': {
        if (!recommendations || tokenUsage) {
          throw new AgentApiError('invalid_response', translate({message: '智能助理返回了无效的 Token 用量事件。'}));
        }
        tokenUsage = parseTokenUsage(event);
        handlers.onUsage?.(tokenUsage);
        return;
      }
      case 'error':
        throw mapStreamError(event);
      default:
        throw new AgentApiError('invalid_response', translate({message: '无法识别智能助理事件：{value0}'}, {value0: String(event.type)}));
    }
  };

  const drainBuffer = () => {
    const boundary = /(?:\r\n|\r|\n){2}/g;
    let consumed = 0;
    let match: RegExpExecArray | null;

    while ((match = boundary.exec(buffer)) !== null) {
      processBlock(buffer.slice(consumed, match.index));
      consumed = match.index + match[0].length;
      if (receivedDone) {
        break;
      }
    }

    buffer = buffer.slice(consumed);
  };

  try {
    while (!receivedDone) {
      const {done, value} = await reader.read();
      buffer += decoder.decode(value, {stream: !done});
      drainBuffer();

      if (done) {
        if (buffer.trim()) {
          processBlock(buffer);
          buffer = '';
        }
        break;
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }

  if (!receivedDone || !session || !quality || !recommendations || !tokenUsage) {
    throw new AgentApiError('invalid_response', translate({message: '智能助理流在完成前意外结束。'}));
  }

  return {
    reply,
    ...session,
    ...quality,
    ...recommendations,
    token_usage: tokenUsage,
  };
}

export async function chat(
  request: ChatRequest,
  signal?: AbortSignal,
  handlers: ChatStreamHandlers = {},
): Promise<ChatResponse> {
  let response: Response;

  try {
    response = await fetch(`${AGENT_API_BASE_PATH}/chat/stream`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        accept: 'text/event-stream',
        'accept-language': AGENT_LANGUAGE,
        'content-type': 'application/json',
      },
      body: JSON.stringify(request),
      signal,
    });
  } catch (error) {
    if (signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
      throw new AgentApiError('cancelled', translate({message: '请求已取消。'}));
    }
    throw new AgentApiError('network', translate({message: '无法连接智能助理服务。'}), {retryable: true});
  }

  if (!response.ok) {
    throw mapHttpError(response, await readPayload(response));
  }

  try {
    return await readChatStream(response, handlers);
  } catch (error) {
    if (error instanceof AgentApiError) {
      throw error;
    }
    if (signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
      throw new AgentApiError('cancelled', translate({message: '请求已取消。'}));
    }
    throw new AgentApiError('network', translate({message: '智能助理流式连接意外中断。'}), {retryable: true});
  }
}

export async function submitFeedback(
  messageId: string,
  request: FeedbackRequest,
  signal?: AbortSignal,
): Promise<FeedbackResponse> {
  const payload = await requestAgent(`/messages/${encodeURIComponent(messageId)}/feedback`, {
    method: 'PUT',
    body: JSON.stringify(request),
    signal,
  });

  return parseFeedbackResponse(payload);
}

export function getAgentErrorMessage(error: unknown): string {
  if (!(error instanceof AgentApiError)) {
    return translate({message: '智能助理遇到未知错误，请稍后重试。'});
  }

  switch (error.kind) {
    case 'invalid_request':
      return translate({message: '问题未被处理，请检查内容后重新提交。'});
    case 'unauthorized':
      return translate({message: '匿名访问暂不可用，请稍后再试。'});
    case 'forbidden':
      return translate({message: '当前访问已被限制，暂时无法继续使用智能助理。'});
    case 'context_limit':
      return translate({message: '当前会话内容过长，请缩短问题或开始新会话。'});
    case 'rate_limited':
      return translate({message: '请求过于频繁或今日额度已用完，请稍后重试。'});
    case 'unavailable':
      return translate({message: '智能助理服务暂不可用，请稍后重试。'});
    case 'network':
      return translate({message: '无法连接智能助理服务，请检查网络后重试。'});
    case 'invalid_response':
      return translate({message: '智能助理返回了无法识别的数据，请稍后重试。'});
    case 'cancelled':
      return translate({message: '本次请求已取消。'});
    default:
      return translate({message: '智能助理未能完成请求，请稍后重试。'});
  }
}
